import React, { useMemo } from 'react';
import { Card, Row, Col, Typography } from 'antd';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Ambassador } from '../../types';
import { arteliaColors } from '../../theme';

const { Title } = Typography;

interface AnalyticsChartsProps {
  ambassadors: Ambassador[];
  filteredAmbassadors: Ambassador[];
}

const AnalyticsCharts: React.FC<AnalyticsChartsProps> = ({
  ambassadors,
  filteredAmbassadors,
}) => {
  // Ambassador Density by Country (Density = ambassadors per 100k hypothetical population)
  const ambassadorDensityData = useMemo(() => {
    const countryCounts = filteredAmbassadors.reduce((acc, ambassador) => {
      const country = ambassador.country;
      acc[country] = (acc[country] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Calculate density score (normalized to show relative concentration)
    const maxCount = Math.max(...Object.values(countryCounts));
    
    return Object.entries(countryCounts)
      .map(([name, count]) => ({ 
        name, 
        count,
        density: Math.round((count / maxCount) * 100) // Normalize to 0-100 scale
      }))
      .sort((a, b) => b.density - a.density)
      .slice(0, 8);
  }, [filteredAmbassadors]);

  // Business Unit Statistics
  const buStats = useMemo(() => {
    return filteredAmbassadors.reduce((acc, ambassador) => {
      const bu = ambassador.businessUnit;
      if (!acc[bu]) {
        acc[bu] = { boosters: 0, keyUsers: 0, total: 0 };
      }
      acc[bu].total++;
      if (ambassador.role === 'Booster') {
        acc[bu].boosters++;
      } else {
        acc[bu].keyUsers++;
      }
      return acc;
    }, {} as Record<string, { boosters: number; keyUsers: number; total: number }>);
  }, [filteredAmbassadors]);

  // Booster Data - Alphabetically ordered
  const boosterData = useMemo(() => {
    return Object.entries(buStats)
      .map(([name, stats]) => ({
        name,
        boosters: stats.boosters,
        keyUsers: stats.keyUsers,
        total: stats.total
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [buStats]);

  // Key Users Data - Alphabetically ordered, exclude zeros
  const keyUserData = useMemo(() => {
    return Object.entries(buStats)
      .map(([name, stats]) => ({
        name,
        boosters: stats.boosters,
        keyUsers: stats.keyUsers,
        total: stats.total
      }))
      .filter(item => item.keyUsers > 0)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [buStats]);

  // Role Distribution Data
  const roleData = useMemo(() => {
    const counts = filteredAmbassadors.reduce((acc, ambassador) => {
      const role = ambassador.role;
      acc[role] = (acc[role] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [filteredAmbassadors]);

  // Country Distribution Data (Top 8)
  const countryData = useMemo(() => {
    const counts = filteredAmbassadors.reduce((acc, ambassador) => {
      const country = ambassador.country;
      acc[country] = (acc[country] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }, [filteredAmbassadors]);

  // Activity Distribution Data (Top 10)
  const activityData = useMemo(() => {
    const counts = filteredAmbassadors.reduce((acc, ambassador) => {
      const activity = ambassador.activity;
      if (activity) {
        acc[activity] = (acc[activity] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [filteredAmbassadors]);

  // Color palette for charts
  const chartColors = [
    arteliaColors.bluePrimary,
    arteliaColors.greenDarker,
    arteliaColors.purpleDarker,
    arteliaColors.redDarker,
    arteliaColors.yellowDarker,
    '#1890ff',
    '#722ed1',
    '#595959',
  ];

  const pieColors = [
    arteliaColors.bluePrimary,
    arteliaColors.greenDarker,
    arteliaColors.purpleDarker,
    arteliaColors.redDarker,
  ];

  return (
    <Row gutter={[24, 24]} justify="center">
      {/* Booster Ambassadors by Business Unit */}
      <Col xs={12}>
        <Card 
          title={
            <Title level={4} style={{ margin: 0, color: arteliaColors.bluePrimary }}>
              Booster Members by Business Unit
            </Title>
          }
          style={{ height: '400px' }}
          styles={{ body: { padding: '20px', height: 'calc(100% - 57px)' } }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={boosterData} margin={{ top: 30, right: 30, left: 20, bottom: 80 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="name" 
                tick={{ fontSize: 11 }}
                interval={0}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis hide />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: `1px solid ${arteliaColors.bluePrimary}`,
                  borderRadius: '6px',
                  fontSize: '13px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                }}
                formatter={(value, name, props) => {
                  const data = props.payload;
                  return [`${value} Boosters`, `Total: ${data.total}`];
                }}
              />
              <Bar 
                dataKey="boosters" 
                fill={arteliaColors.greenDarker}
                radius={[4, 4, 0, 0]}
                strokeWidth={1}
                stroke={arteliaColors.greenDarker}
                label={{ 
                  position: 'top', 
                  fill: arteliaColors.bluePrimary, 
                  fontSize: 12, 
                  fontWeight: 600 
                }}
              />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </Col>

      {/* Key Users by Business Unit */}
      <Col xs={12}>
        <Card 
          title={
            <Title level={4} style={{ margin: 0, color: arteliaColors.bluePrimary }}>
              Key Users by Business Unit
            </Title>
          }
          style={{ height: '400px' }}
          styles={{ body: { padding: '20px', height: 'calc(100% - 57px)' } }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={keyUserData} margin={{ top: 30, right: 30, left: 20, bottom: 80 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="name" 
                tick={{ fontSize: 11 }}
                interval={0}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis hide />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: `1px solid ${arteliaColors.bluePrimary}`,
                  borderRadius: '6px',
                  fontSize: '13px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                }}
                formatter={(value, name, props) => {
                  const data = props.payload;
                  return [`${value} Key Users`, `Total: ${data.total}`];
                }}
              />
              <Bar 
                dataKey="keyUsers" 
                fill={arteliaColors.bluePrimary}
                radius={[4, 4, 0, 0]}
                strokeWidth={1}
                stroke={arteliaColors.bluePrimary}
                label={{ 
                  position: 'top', 
                  fill: arteliaColors.bluePrimary, 
                  fontSize: 12, 
                  fontWeight: 600 
                }}
              />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </Col>
    </Row>
  );
};

export default AnalyticsCharts;